<template>
  <div>
    <div v-if="!errorPage">
      <span
        :class="`w-1/5 shadow-lg p-3 -mt-20 text-white text-sm text-center absolute ${responseBg}`"
        style="left: 77%"
        v-if="success"
      >
        {{ response }}
      </span>

      <Card class="tax-dashboard-card mt-6">
        <div class="tax-dashboard_inner">
          <div class="tax-stats-row">
            <div class="tax-stat-lead">
              <Icon
                icon-name="users"
                size="s"
                class-name="tax-stat-lead_icon text-darkPurple"
              />
              <div class="tax-stat-lead_copy">
                <p class="tax-stat__label">Total employee count</p>
                <p class="tax-stat__value">{{ totalEmployeesDisplay }}</p>
              </div>
            </div>
            <div class="tax-stat-group">
              <div class="tax-stat-block">
                <Icon
                  icon-name="users"
                  size="s"
                  class-name="tax-stat-block_icon text-flame"
                />
                <div class="tax-stat-block_copy tax-stat-block_copy--grow">
                  <p class="tax-stat__label">TIN</p>
                  <div class="tax-tin-row">
                    <p class="tax-stat__value tax-stat__value--tin">
                      {{ tinSummaryDisplay }}
                    </p>

                  </div>
                </div>
              </div>
              <div class="tax-stat-block">
                <Icon
                  icon-name="users"
                  size="s"
                  class-name="tax-stat-block_icon text-flame"
                />
                <div class="tax-stat-block_copy">
                  <p class="tax-stat__label">Number of tax authority</p>
                  <p class="tax-stat__value">{{ taxAuthorityCountDisplay }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="tax-dashboard_rule" aria-hidden="true" />

          <div class="h-[30px]">
            <card-footer
              reloadcard
              :show-map-pin="false"
              :showcalendar="false"
              @sortType="handleSort($event)"
              @searchResult="handleSearch($event)"
              @reload="handleReload"
            />
          </div>

          <div v-if="loading && !hasLoadedOnce" class="tax-body-loading">
            <loader size="xxs" :loader-image="false" />
          </div>

          <div
            v-else-if="!taxRows.length"
            class="tax-empty-card"
          >
            <Icon
              icon-name="tax-empty"
              size=""
              class-name="tax-empty-card__illustration"
            />
            <p class="tax-empty-card__text">
              A list of different Tax Authorities based on profiled locations
              will eventually show up here. If you haven't, add locations to
              populate this area accordingly.
            </p>
          </div>

          <div v-else class="tax-table-wrap">
            <STable
              :headers="taxHeader"
              :items="taxRows"
              aria-label="tax authorities"
              :loading="loading"
              :pagination-list="taxPagination"
              has-number
              class="w-full mb-4"
              @page="handlePage"
              @itemsPerPage="handleItemsPerPage"
            >
              <template v-slot:item="{ item }">
                <span v-if="item.name" class="tax-cell">{{
                  item.data.name
                }}</span>
                <span v-if="item.code" class="tax-cell">{{
                  item.data.code
                }}</span>
                <span v-if="item.state" class="tax-cell">{{
                  item.data.state
                }}</span>
                <span v-if="item.employeesCount" class="tax-cell">{{
                  item.data.employeesCount
                }}</span>
                <span v-if="item.status" class="tax-cell">{{
                  formatStatus(item.data.status)
                }}</span>
                <div v-if="item.tin" class="tax-cell tax-cell--tin">
                  <div v-if="item.data.editable" class="flex items-center gap-2">
                    <c-text
                      variant="w-36"
                      placeholder="Enter TIN"
                      v-model="item.data.tin"
                    />
                    <div @click="saveOrgTin(item.data)">
                      <Icon
                        icon-name="save"
                        class-name="text-flame cursor-pointer"
                        size="xs"
                      />
                    </div>
                  </div>
                  <p v-else class="m-0">{{ item.data.tin || "—" }}</p>
                </div>
                <span v-if="item.id" class="tax-cell tax-cell--actions flex">
                  <div
                    v-if="!item.data.editable"
                    class="cursor-pointer"
                    @click="handleEditable(item.data.id)"
                  >
                    <Icon
                      icon-name="edit"
                      class-name="text-blueCrayola"
                      size="xs"
                    />
                  </div>
                  <div
                    v-else
                    class="cursor-pointer"
                    @click="item.data.editable = false"
                  >
                    <Icon icon-name="close" size="xs" />
                  </div>
                </span>
              </template>
            </STable>
          </div>
        </div>
      </Card>

      <RightSideBar
        v-if="openModal"
        @submit="remitTax"
        @close="$emit('closeTax')"
        submit="Save"
        :button-class="`bg-dynamicBackBtn text-white`"
        :disabled-button="disableBtn"
      >
        <template v-slot:title>
          <p class="text-darkPurple">Remittance</p>
        </template>
        <template v-slot:subtitle>
          <p class="font-semibold text-darkPurple">Select remittance option</p>
          <p class="text-darkPurple">
            The system automatically calculates individual PAYE per location and
            can help electronically remit to the relevant authorities.
          </p>
          <radio-button
            :options="categoryArray"
            col-span="col-span-12"
            class="m-4 text-darkPurple"
            row-gap="gap-y-5"
            v-model="remittance"
          />
        </template>
      </RightSideBar>
    </div>

    <div v-else>
      <ErrorPage />
    </div>
  </div>
</template>

<script>
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Loader from "@scelloo/cloudenly-ui/src/components/loader";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import Card from "@/components/Card";
import CardFooter from "@/components/CardFooter";
import RightSideBar from "@/components/RightSideBar";
import RadioButton from "@/components/RadioButton";
import Icon from "@/components/Icon";
import ErrorPage from "@/modules/Admin/error403";

export default {
  name: "Tax",
  components: {
    Card,
    STable,
    Loader,
    ErrorPage,
    RightSideBar,
    CText,
    CardFooter,
    RadioButton,
    Icon,
  },
  props: {
    openModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      disableBtn: false,
      taxRows: [],
      taxSummary: {
        numberOfTotalEmployees: null,
        tin: "",
        numberOfTaxAuthorities: null,
      },
      searchWord: null,
      sortType: null,
      loading: true,
      hasLoadedOnce: false,
      errorPage: false,
      remittance: "",
      response: "",
      responseBg: "",
      success: false,
      numberOfPage: null,
      itemsPerPage: null,
      taxMeta: { page: 1, total: 0, perPage: 50 },
      taxHeader: [
        { title: "TAX AUTHORITY", value: "name", width: 22 },
        { title: "AUTHORITY CODE", value: "code", width: 14 },
        { title: "STATE", value: "state", width: 12 },
        { title: "EMPLOYEES COUNT", value: "employeesCount", width: 12 },
        { title: "STATUS", value: "status", width: 10 },
        { title: "TIN", value: "tin", width: 14 },
        { title: "", value: "id", image: true },
      ],
      categoryArray: [
        {
          name: "Yes, set me up for automatic remittance",
          radioName: "tax",
          value: true,
        },
        {
          name: "No, do not set me up for automatic remittance",
          radioName: "tax",
          value: false,
        },
      ],
    };
  },
  computed: {
    taxPagination() {
      return this.taxMeta;
    },
    totalEmployeesDisplay() {
      const n = this.taxSummary.numberOfTotalEmployees;
      if (n != null && n !== "") {
        return n;
      }
      return 0;
    },
    tinSummaryDisplay() {
      const t = this.taxSummary.tin;
      if (t != null && String(t).trim() !== "") {
        return String(t).trim();
      }
      return "----";
    },
    canCopySummaryTin() {
      const t = this.taxSummary.tin;
      return t != null && String(t).trim() !== "" && String(t).trim() !== "----";
    },
    taxAuthorityCountDisplay() {
      const n = this.taxSummary.numberOfTaxAuthorities;
      if (n != null && n !== "") {
        return n;
      }
      return this.taxRows.length;
    },
  },
  methods: {
    formatStatus(status) {
      if (status == null || status === "") {
        return "—";
      }
      const s = String(status).toLowerCase();
      if (s === "onsite" || s === "remote") {
        return s.charAt(0).toUpperCase() + s.slice(1);
      }
      return String(status);
    },
    copySummaryTin() {
      const text = String(this.taxSummary.tin || "").trim();
      if (!text) {
        return;
      }
      const done = () => {
        this.$toasted.success("TIN copied to clipboard", { duration: 2000 });
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          this.$toasted.info("Could not copy automatically", {
            duration: 3000,
          });
        });
      } else {
        this.$toasted.info(text, { duration: 4000 });
      }
    },
    handleReload() {
      this.fetchTaxAndSummary(this.buildQueryString());
    },
    handlePage(pageNumber) {
      this.numberOfPage = pageNumber;
      this.fetchTaxAndSummary(this.buildQueryString());
    },
    handleItemsPerPage(n) {
      this.itemsPerPage = n;
      this.fetchTaxAndSummary(this.buildQueryString());
    },
    buildQueryString() {
      const parts = [];
      if (this.sortType) {
        parts.push(`sort=${this.sortType}`);
      }
      if (this.searchWord) {
        parts.push(`search=${encodeURIComponent(this.searchWord)}`);
      }
      let page = 1;
      if (this.numberOfPage != null) {
        page = this.numberOfPage;
      } else if (this.taxMeta && this.taxMeta.page != null) {
        page = this.taxMeta.page;
      }
      parts.push(`page=${page}`);
      if (this.itemsPerPage) {
        parts.push(`perPage=${this.itemsPerPage}`);
      }
      return parts.join("&");
    },
    async handleEditable(id) {
      try {
await this.$handlePrivilege("tax", "updateTaxIdentityNumber");
        const result = this.taxRows.find((tax) => tax.id === id);
        if (result) {
          result.editable = true;
        }
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    handleSort(sortType) {
      const changeSortType = sortType.split(":");
      let result;
      if (changeSortType[0] === "createdAt") {
        result = sortType;
      } else {
        result = `name:${changeSortType[1]}`;
      }
      this.sortType = result;
      this.fetchTaxAndSummary(this.buildQueryString());
    },
    handleSearch(searchWord) {
      if (searchWord) {
        this.searchWord = searchWord;
      } else {
        this.searchWord = null;
      }
      this.fetchTaxAndSummary(this.buildQueryString());
    },
    saveOrgTin(taxData) {
      // eslint-disable-next-line no-param-reassign
      taxData.editable = false;
      const payload = {
        orgId: this.$orgId,
        taxAuthorityId: taxData.id,
        tin: taxData.tin,
      };
      this.$_addOrgTaxAuthorityTin(payload)
        .then(() => {
          this.fetchTaxAndSummary(this.buildQueryString());
        })
        .catch((err) => {
          this.$toasted.error(err && err.message ? err.message : String(err), {
            duration: 5000,
          });
        });
    },
    remitTax() {
      this.disableBtn = true;
      const payload = {
        orgId: this.$orgId,
        canRemitTaxAutomatically: this.remittance,
      };
      this.$_saveTaxRemittance(payload)
        .then((result) => {
          this.$emit("closeTax");
          this.$toasted.success(`${result.data.message}`, { duration: 3000 });
          this.disableBtn = false;
        })
        .catch((err) => {
          this.$toasted.error("Action not successful", { duration: 3000 });
          this.disableBtn = false;
          throw new Error(err);
        });
    },
    mapSummaryPayload(raw) {
      const base = raw && typeof raw === "object" ? raw : {};
      const s =
        base.data != null &&
        typeof base.data === "object" &&
        !Array.isArray(base.data)
          ? base.data
          : base;
      return {
        numberOfTotalEmployees:
          s.numberOfTotalEmployees ??
          s.totalEmployees ??
          s.totalEmployeeCount ??
          s.employeeCount ??
          null,
        tin:
          s.tin ??
          s.organizationTin ??
          s.orgTin ??
          s.primaryTin ??
          "",
        numberOfTaxAuthorities:
          s.numberOfTaxAuthorities ??
          s.taxAuthorityCount ??
          s.totalTaxAuthorities ??
          null,
      };
    },
    mapAuthorityRow(item) {
      const emp =
        item.employeesCount ??
        item.employeeCount ??
        item.numberOfEmployees ??
        item.employees ??
        "—";
      const status =
        item.status ??
        item.workStatus ??
        item.employmentStatus ??
        item.locationType ??
        "";
      return {
        id: item.id,
        name: item.name,
        code: item.code,
        state: item.state && item.state.name ? item.state.name : "",
        label: "PAYE",
        tin: item.tin ? item.tin.tin : "",
        tinId: item.tin ? item.tin.id : "",
        employeesCount: emp,
        status,
        editable: false,
      };
    },
    async fetchTaxAndSummary(queryStr) {
      this.loading = true;
      const listQuery = queryStr || "";
      try {
        const [listRes, sumRes] = await Promise.all([
          this.$_getOrgTaxAuthorityTins(listQuery),
          this.$_getOrgTaxTinsSummary().catch(() => ({ data: {} })),
        ]);
        const data = listRes.data || {};
        const authorities = data.taxAuthorities || [];
        this.taxRows = authorities.map((item) => this.mapAuthorityRow(item));
        this.taxMeta = {
          ...(data.meta || {}),
          total:
            data.meta && data.meta.total != null
              ? data.meta.total
              : this.taxRows.length,
          page:
            data.meta && data.meta.page != null ? data.meta.page : 1,
          perPage:
            data.meta && data.meta.perPage != null
              ? data.meta.perPage
              : this.itemsPerPage || 50,
        };
        this.taxSummary = this.mapSummaryPayload(sumRes.data || {});
      } catch (e) {
        this.$toasted.error(
          (e && e.message) || "Failed to load tax authorities",
          { duration: 5000 }
        );
      } finally {
        this.loading = false;
        this.hasLoadedOnce = true;
      }
    },
  },
  async mounted() {
    try {
      await this.$handlePrivilege("tax", "viewRegister");
      await this.fetchTaxAndSummary(this.buildQueryString());
    } catch (error) {
      this.errorPage = true;
    }
  },
};
</script>

<style scoped>
.tax-dashboard-card {
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(45, 49, 66, 0.06),
    0 1px 3px rgba(8, 7, 8, 0.1);
}

.tax-dashboard_inner {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;
}

.tax-dashboard_rule {
  width: 100%;
  height: 0;
  border: 0;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 0;
}

.tax-stats-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  width: 100%;
}

.tax-stat-lead {
  flex: 0 0 308px;
  box-sizing: border-box;
  max-width: 100%;
  min-height: 65px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 24px;
  background: #fcf4ff;
  border-radius: 16px;
}

.tax-stat-lead_icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  color: #985EAF;
  fill: #985EAF;
  stroke: #985EAF;
}

.tax-stat-lead_copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.tax-stat-group {
  flex: 1 1 220px;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  min-width: 0;
}

.tax-stat-block {
  flex: 1 1 0;
  min-width: 140px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  padding: 16px 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow:
  -153px 231px 77px 0 rgba(0, 0, 0, 0.00), -98px 148px 71px 0 rgba(0, 0, 0, 0.01), -24px 37px 44px 0 rgba(0, 0, 0, 0.05);

}

.tax-stat-block_icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.tax-stat-block_copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.tax-stat-block_copy--grow {
  flex: 1 1 0;
  min-width: 0;
}

.tax-stat__label {
  margin: 0;
  font-size: 10px;
  font-weight: 900;
  line-height: 1.4;
  letter-spacing: 0.35px;
  text-transform: uppercase;
  color: #878e99;
}

.tax-stat__value {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.24px;
  color: #333;
}

.tax-stat__value--tin {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.tax-tin-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  width: 100%;
  min-width: 0;
}

.tax-copy-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
}

.tax-copy-btn:hover {
  background: rgba(33, 118, 255, 0.08);
}

@media (max-width: 900px) {
  .tax-stat-lead {
    flex: 1 1 100%;
  }

  .tax-stat-group {
    flex: 1 1 100%;
  }
}

.tax-toolbar-footer-wrap {
  width: 100%;
  min-width: 0;
}

.tax-toolbar-footer-wrap :deep(.mt-6) {
  margin-top: 0 !important;
}

.tax-toolbar-footer-wrap :deep(.-mt-2) {
  margin-top: 0 !important;
}

.tax-toolbar-footer-wrap :deep(.flex.w-full) {
  align-items: center;
}

.tax-toolbar-footer-wrap :deep(.flex.ml-3.-mt-2) {
  background: #f7f7ff;
  border-radius: 8px;
  height: 40px;
  padding: 0 15px;
  box-sizing: border-box;
  align-items: center;
}

.tax-toolbar-footer-wrap :deep(.search) {
  width: 320px !important;
  max-width: 100%;
}

.tax-toolbar-footer-wrap :deep(.dropDownMenuWrapper.w-10) {
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  background: #fff;
}

.tax-toolbar-footer-wrap :deep(.flex.ml-auto .cardClass) {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}

.tax-body-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 160px;
}

.tax-empty-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 280px;
  padding: 24px 20px 32px;
  background: #fff;
  border-radius: 16px;
  box-shadow:
    0 1px 2px rgba(45, 49, 66, 0.06),
    0 1px 3px rgba(8, 7, 8, 0.1);
}

.tax-empty-card__illustration {
  width: 220px;
  height: auto;
  max-width: 100%;
}

.tax-empty-card__text {
  margin: 12px 0 0;
  max-width: 490px;
  font-size: 16px;
  line-height: 1.2;
  letter-spacing: 0.24px;
  color: #878e99;
}

.tax-table-wrap :deep(tbody tr:nth-child(even)) {
  background: #f7f7ff;
}

.tax-cell {
  font-size: 14px;
  line-height: 1.3;
  color: #321c3b;
}

.tax-cell--tin {
  min-width: 120px;
}

.tax-cell--actions {
  justify-content: flex-end;
}
</style>
